import type { PluginListenerHandle } from "@capacitor/core";
import { JuspaySdkPayload } from "@onmo/graphql/graphql";
import {
  CANCEL_ERROR_CODES,
  CANCEL_STATUSES,
  FAILED_STATUSES,
  HYPER_SDK_DIV_ID,
  PENDING_STATUSES,
  SUCCESS_STATUSES,
} from "../constants/juspay";
import { StatusCallbacks } from "../types/payment";
import { JuspayHyper } from "./juspayHyperPlugin";

export interface HyperServicesInstance {
  initiate: (
    payload: Record<string, unknown>,
    callback: (eventData: string | Record<string, unknown>) => void,
  ) => void;

  process: (payload: Record<string, unknown>) => void;

  isInitialised: () => boolean;

  terminate?: () => void;
}

declare global {
  interface Window {
    HyperServices: new () => HyperServicesInstance;
  }
}

let sdkLoadPromise: Promise<void> | null = null;

export const loadJuspaySDK = (): Promise<void> => {
  if (window.HyperServices) return Promise.resolve();

  if (sdkLoadPromise) return sdkLoadPromise;

  sdkLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://payments.juspay.in/payment-page/HyperServices.js";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      sdkLoadPromise = null;
      reject(new Error("Failed to load Juspay SDK"));
    };
    document.body.appendChild(script);
  });

  return sdkLoadPromise;
};

export const routeJuspayStatus = (
  status: string | undefined,
  errorCode: string | undefined,
  payload: Record<string, unknown>,
  orderId: string,
  callbacks: StatusCallbacks,
) => {
  if (
    (status && CANCEL_STATUSES.has(status)) ||
    (errorCode && CANCEL_ERROR_CODES.has(errorCode))
  ) {
    callbacks.onCancel();
    return;
  }

  if (
    status &&
    (SUCCESS_STATUSES.has(status) || PENDING_STATUSES.has(status))
  ) {
    callbacks.onVerify(orderId);
    return;
  }

  if (status && FAILED_STATUSES.has(status)) {
    callbacks.onFailure(payload);
    return;
  }

  console.warn("[JuspayCheckout] unknown status:", status, payload);
  callbacks.onVerify(orderId);
};

export const createNativeHyperAdapter = (): HyperServicesInstance => {
  let initialised = false;
  let listener: PluginListenerHandle | null = null;
  let userCallback: ((data: string | Record<string, unknown>) => void) | null =
    null;

  const dispatch = (data: Record<string, unknown>) => {
    if ((data as { event?: string }).event === "initiate_result") {
      const status = (data as { payload?: { status?: string } }).payload
        ?.status;
      initialised = !status || String(status).toUpperCase() === "SUCCESS";
    }
    userCallback?.(data);
  };

  const failEvent = (event: string, err: unknown) =>
    dispatch({
      event,
      payload: { status: "FAILED", error: String(err) },
    });

  return {
    initiate(payload, callback) {
      userCallback = callback;
      JuspayHyper.addListener("juspayEvent", (e) =>
        dispatch(e as Record<string, unknown>),
      ).then((h) => {
        listener = h;
      });
      JuspayHyper.initiate(
        payload as unknown as Parameters<typeof JuspayHyper.initiate>[0],
      ).catch((err) => failEvent("initiate_result", err));
    },
    process(payload) {
      JuspayHyper.process(
        payload as unknown as Parameters<typeof JuspayHyper.process>[0],
      ).catch((err) => failEvent("process_result", err));
    },
    isInitialised: () => initialised,
    terminate() {
      listener?.remove();
      listener = null;
      userCallback = null;
      JuspayHyper.terminate().catch((err) => {
        console.error("Failed to terminate Juspay plugin", err);
      });
    },
  };
};

export const buildInitPayload = (
  sdkPayload: JuspaySdkPayload,
  isNative: boolean,
): { requestId: string; service: string; payload: Record<string, unknown> } => {
  const backendPayload = sdkPayload.payload as Record<string, unknown>;
  const environment = backendPayload.environment ?? "production";

  const payload: Record<string, unknown> = isNative
    ? {
        ...backendPayload,
        action: "initiate",
        environment,
        betaAssets: false,
      }
    : {
        action: "initiate",
        hyperSDKDiv: HYPER_SDK_DIV_ID,
        integrationType: "iframe",
        environment,
        merchantId: backendPayload.merchantId,
        clientId: backendPayload.clientId,
      };

  return {
    requestId: sdkPayload.requestId,
    service: sdkPayload.service,
    payload,
  };
};

export const handleSdkEvent = (
  eventData: string | Record<string, unknown>,
  hyper: HyperServicesInstance,
  sdkPayload: JuspaySdkPayload,
  callbacks: StatusCallbacks,
  orderId: string,
) => {
  const event =
    typeof eventData === "string" ? JSON.parse(eventData) : eventData;
  const eventName = event?.event as string;

  if (eventName === "initiate_result") {
    if (hyper.isInitialised()) {
      hyper.process({
        requestId: sdkPayload.requestId,
        service: sdkPayload.service,
        payload: sdkPayload.payload as Record<string, unknown>,
      });
    } else {
      callbacks.onFailure({ error: "SDK failed to initialise" });
    }
    return;
  }

  if (eventName === "process_result") {
    const payload = (event?.payload ?? {}) as Record<string, unknown>;
    routeJuspayStatus(
      payload.status as string | undefined,
      payload.errorCode as string | undefined,
      payload,
      orderId,
      callbacks,
    );
    return;
  }

  if (eventName === "user_event") {
    console.log("[JuspayCheckout] user_event:", event);
  }
};
